import { useEffect, useState } from "react"

import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { Alert, Card, Flex, Space, Spin, Typography } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect, useDispatch } from "react-redux"
import { useLocation } from "react-router"
import { useNavigate } from "react-router-dom"

import { AdyenDropIn } from "../Components/AdyenDropIn/AdyenDropIn"
import ButtonComponent from "../Components/Button"
import Icon from "../Icon"
import AppUtils, { useQuery } from "../Utils/utils"
import { balanceActions } from "./Balance/redux/balance.slice"

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
const environment = process.env.REACT_APP_ENVIRONMENT === "prod" ? "live" : "test"
const { Text } = Typography

const AddCard = ({
  auth: { accessToken },
  userInfo,
  businessAccount,
  nav,
  adyenSessionData,
  ...props
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  let paymentServiceBusinessAccountId = businessAccount?.payment_service_business_account_id || null

  const { search, pathname } = useLocation()
  let query = useQuery(search)

  let sessionId = query.get("session_id")
  const isManualTopUp = pathname === "/manual-top-up"

  const [clientSecret, setClientSecret] = useState("")
  const [adyenData, setAdyenData] = useState({})
  const [provider, setProvider] = useState()
  const [isLoading, setIsLoading] = useState(false)
  const unsupportedCardsMessage =
    provider === "adyen"
      ? t("At this time, we do not accept American Express.")
      : t("At this time, we do not accept American Express and Discover.")

  const navigateToBilling = () => {
    navigate("/settings/balance")
  }

  const handleSendAnalytics = (url) => {
    AppUtils.goLink(url)
  }

  const options = { clientSecret, onComplete: navigateToBilling }

  useEffect(() => {
    if (!clientSecret && !sessionId && !isManualTopUp) {
      setIsLoading(true)
      fetch(`${process.env.REACT_APP_BASE_API_ROUTE}/payments/create-checkout-session/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `JWT ${accessToken}`
        },
        body: JSON.stringify({
          payment_service_business_account_id: paymentServiceBusinessAccountId,
          business_account_id: userInfo?.current_business_account_id,
          return_url: process.env.REACT_APP_BASE_URI + "/settings/balance",
          is_onboarding: false
        })
      })
        .then((res) => res.json())
        .then((data) => {
          if (data.provider !== "stripe") {
            setAdyenData(data.adyen_session)
          }
          setProvider(data.provider)
          setClientSecret(data?.["checkout_session"]?.client_secret)
        })
        .catch((error) => {
          console.error("Error:", error)
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  useEffect(() => {
    if (!adyenSessionData || !isManualTopUp) return

    setAdyenData(adyenSessionData.adyen_session)
    setProvider(adyenSessionData.provider)
  }, [adyenSessionData, isManualTopUp])

  useEffect(() => {
    return () => dispatch(balanceActions.clearAdyenSessionData())
  }, [])

  const onError = (e) => {
    const body = {
      resultCode: e.resultCode,
      sessionResult: e.sessionResult,
      adyen_session_id: adyenData.id
    }

    const url = `${process.env.REACT_APP_BASE_API_ROUTE}/payments/balance/`
    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${accessToken}`
      },
      body: JSON.stringify(body)
    })
      .then((res) => console.log("res", res))
      .catch((e) => console.error(e))
  }

  const onSuccess = (res) => {
    const url = isManualTopUp
      ? `${process.env.REACT_APP_BASE_API_ROUTE}/payments/balance/`
      : `${process.env.REACT_APP_BASE_API_ROUTE}/payments/attach-payment-method/`

    const manualPayload =
      isManualTopUp && adyenSessionData
        ? {
            topup_type: "manual",
            adyen_result_code: res?.resultCode ?? null,
            adyen_psp_reference: res?.pspReference ?? null,
            invoice_id: adyenSessionData.invoice_id,
            amount: Number(adyenSessionData?.adyen_session?.amount?.value ?? 0) / 100
          }
        : null

    const payload = {
      ...(manualPayload ?? {}),
      adyen_session_id: adyenData?.id ?? null,
      adyen_session_result: res?.sessionResult ?? null
    }

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `JWT ${accessToken}`
      },
      body: JSON.stringify(payload)
    })
      .then((res) => res.json())
      .then((res) => {
        if (adyenSessionData) dispatch(balanceActions.clearAdyenSessionData())
        if (res.redirect_url) {
          handleSendAnalytics(res.redirect_url)
          return
        }
        navigateToBilling()
      })
      .catch(console.error)
  }

  const isMobile = nav?.isMobile
  const isDark = nav?.theme === "dark"

  // Kept as a single tree (styled by isMobile, not branched into two separate
  // `return`s) - Stripe's EmbeddedCheckout can't survive being unmounted and
  // remounted for the same clientSecret, which is exactly what happened when a
  // desktop<->mobile switch swapped between two independent JSX subtrees each
  // declaring their own EmbeddedCheckoutProvider.
  return (
    <Flex
      vertical={isMobile}
      align={isMobile ? "stretch" : "center"}
      justify={isMobile ? "flex-start" : "center"}
      className={isMobile ? "add-card-mobile" : undefined}
      style={isMobile ? undefined : { padding: 32 }}
    >
      {isMobile ? (
        <Flex
          align="center"
          justify="center"
          style={{
            padding: "8px 24px",
            borderBottom: `1px solid ${isDark ? "rgba(255, 255, 255, 0.12)" : "#e1e1e2"}`,
            position: "relative",
            flexShrink: 0
          }}
        >
          <ButtonComponent
            type="text"
            style={{ position: "absolute", left: 8 }}
            icon={<Icon color={isDark ? "#FFF" : "#000"} type="arrow_left" />}
            onClick={navigateToBilling}
          />
          <Text style={{ fontWeight: 500, fontSize: 14 }}>{t("Back to balances")}</Text>
        </Flex>
      ) : (
        <ButtonComponent
          type={"text"}
          size="large"
          className={"flex center"}
          style={{
            position: "absolute",
            top: 32,
            left: 32,
            zIndex: 10
          }}
          icon={<Icon color={isDark ? "#FFF" : "#000"} type="arrow_left" />}
          onClick={navigateToBilling}
        >
          <Text>{t("Return to the Balance page")}</Text>
        </ButtonComponent>
      )}

      <Card
        className="login-card"
        bordered={!isMobile}
        style={
          isMobile
            ? { width: "100%", flex: 1, boxShadow: "none", background: isDark ? "#141414" : "#fff" }
            : { width: 400, marginTop: 30 }
        }
        styles={isMobile ? { body: { padding: "16px 24px" } } : undefined}
      >
        {(isMobile || !isManualTopUp) && (
          <Alert
            className="primary"
            type="info"
            icon={<Icon type="circle_info" style={{ color: "#1448CB" }} />}
            showIcon
            message={
              isMobile ? (
                <Space direction="vertical">
                  <span>
                    {t(
                      "No charges will be applied. This is for us to verify your card for future use."
                    )}
                  </span>
                  <span>{t("At this time, we do not accept American Express and Discover.")}</span>
                </Space>
              ) : (
                <Space direction="vertical">
                  <span>
                    {t(
                      "No charges will be applied. Your bank may place a temporary authorization to verify your card."
                    )}
                  </span>
                  <span>{unsupportedCardsMessage}</span>
                </Space>
              )
            }
            style={{ marginBottom: 16 }}
          />
        )}
        {!isMobile && <Spin size="large" spinning={isLoading} style={{ margin: "auto" }} />}
        {provider === "stripe" && (
          <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        )}
        {provider === "adyen" && (
          <AdyenDropIn
            session={{ id: adyenData.id, session: adyenData.sessionData }}
            clientKey={adyenData.clientKey}
            amount={adyenData.amount}
            environment={environment}
            countryCode={adyenData.countryCode}
            locale={adyenData.shopperLocale}
            recurringProcessingModel={adyenData.recurringProcessingModel}
            onSuccess={onSuccess}
            onError={onError}
          />
        )}
      </Card>
    </Flex>
  )
}

AddCard.propTypes = {
  businessAccount: PropTypes.object
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  auth: state.auth,
  userInfo: state.auth.userInfo,
  businessAccount: state.user.businessAccount,
  adyenSessionData: state.balance.adyenSessionData
})

export default connect(mapStateToProps)(AddCard)
